import { useId, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { smoothPath } from "@signet/ui";
import { api } from "./api.ts";
import { compact } from "./format.ts";

/* ──────────────────────────────────────────────────────────────────────────
   What opens under an Instagram account's row when it is clicked: three
   figures across the top, then the account's followers or views day by day.
   Jay pointed at instrack's profile page for the idea (2026-10-14). IG
   Suicide's Growth tables and a creator's Reach panel both open this one, so
   the two never draw the same account differently.

   One line on one axis at a time, Followers or Views, because the two sit
   thousands of times apart and a second scale would make either look flat.
   The axis is fitted to the line rather than starting at zero: a throwaway
   account going from 1,200 to 1,500 followers is the whole story, and from
   zero it reads as a flat line.
   ────────────────────────────────────────────────────────────────────────── */

export type TrendDay = { day: string; followers: number | null; views: number | null };

export type AccountTrendFigure = { label: string; value: ReactNode; caption?: ReactNode; tone?: "up" | "down" };

type Metric = "followers" | "views";

const METRIC: Record<Metric, { label: string; colour: string }> = {
  followers: { label: "Followers", colour: "var(--green)" },
  views: { label: "Views", colour: "var(--violet)" },
};

const dayLabel = (day: string) => new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** Round the axis out to steps a person would write, so its labels are 1.2K and 1.4K, not 1,187 and 1,433. */
function niceRange(low: number, high: number): { bottom: number; top: number; ticks: number[] } {
  if (low === high) {
    const pad = Math.max(1, Math.abs(high) * 0.05);
    low -= pad;
    high += pad;
  }
  const raw = (high - low) / 3;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((s) => s >= raw) ?? raw;
  const bottom = Math.max(0, Math.floor(low / step) * step);
  const top = Math.ceil(high / step) * step;
  const ticks: number[] = [];
  for (let t = bottom; t <= top + step / 2; t += step) ticks.push(t);
  return { bottom, top, ticks };
}

export function TrendChart({ days, metric }: { days: TrendDay[]; metric: Metric }) {
  const gradient = useId();
  const [hovered, setHovered] = useState<number | null>(null);
  const points = days.filter((d) => d[metric] !== null).map((d) => ({ day: d.day, value: d[metric] as number }));
  if (points.length < 2) {
    return <p className="signet-trend-empty">{points.length ? "One reading so far. The line starts at the second." : "No readings yet."}</p>;
  }
  const values = points.map((p) => p.value);
  const { bottom, top, ticks } = niceRange(Math.min(...values), Math.max(...values));
  const span = top - bottom || 1;
  const x = (i: number) => (i / (points.length - 1)) * 100;
  const y = (v: number) => ((top - v) / span) * 100;
  const coords = points.map((p, i) => ({ x: x(i), y: y(p.value) }));
  const line = smoothPath(coords, 0.4);
  const area = `${line} L 100 100 L 0 100 Z`;
  const colour = METRIC[metric].colour;
  const active = hovered === null ? null : points[hovered];
  const middle = Math.floor((points.length - 1) / 2);

  return (
    <div className="signet-trend-chart" style={{ "--signet-trend-colour": colour } as React.CSSProperties}>
      <div className="signet-trend-axis" aria-hidden="true">
        {[...ticks].reverse().map((t) => (
          <span key={t} style={{ top: `${y(t)}%` }}>{compact(t)}</span>
        ))}
      </div>
      <div
        className="signet-trend-plot"
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const fraction = box.width ? (event.clientX - box.left) / box.width : 0;
          setHovered(Math.max(0, Math.min(points.length - 1, Math.round(fraction * (points.length - 1)))));
        }}
        onPointerLeave={() => setHovered(null)}
      >
        {ticks.map((t) => (
          <i key={t} className="signet-trend-grid" style={{ top: `${y(t)}%` }} />
        ))}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={`${METRIC[metric].label} from ${dayLabel(points[0].day)} to ${dayLabel(points.at(-1)!.day)}: ${compact(values[0])} to ${compact(values.at(-1)!)}`}>
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={colour} stopOpacity=".26" />
              <stop offset="100%" stopColor={colour} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gradient})`} />
          <path d={line} fill="none" stroke={colour} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
        {active && hovered !== null ? (
          <>
            <i className="signet-trend-cross" style={{ left: `${x(hovered)}%` }} />
            <i className="signet-trend-dot" style={{ left: `${x(hovered)}%`, top: `${y(active.value)}%` }} />
            <div className={`signet-trend-tip${x(hovered) > 70 ? " left" : ""}`} style={{ left: `${x(hovered)}%` }}>
              <span>{dayLabel(active.day)}</span>
              <strong>{active.value.toLocaleString()}</strong>
              {hovered > 0 ? (
                <small className={active.value - points[hovered - 1].value >= 0 ? "up" : "down"}>
                  {active.value - points[hovered - 1].value >= 0 ? "+" : ""}{(active.value - points[hovered - 1].value).toLocaleString()} on the reading before
                </small>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
      <div className="signet-trend-days" aria-hidden="true">
        <span>{dayLabel(points[0].day)}</span>
        {points.length > 4 ? <span>{dayLabel(points[middle].day)}</span> : null}
        <span>{dayLabel(points.at(-1)!.day)}</span>
      </div>
    </div>
  );
}

/** The whole drop-down: the figures, the Followers / Views switch and the chart. `trendUrl` answers { days }. */
export function AccountTrend({ trendUrl, figures }: { trendUrl: string; figures: AccountTrendFigure[] }) {
  const [metric, setMetric] = useState<Metric>("followers");
  const trend = useQuery({ queryKey: ["account-trend", trendUrl], queryFn: () => api<{ days: TrendDay[] }>(trendUrl), staleTime: 5 * 60_000 });
  return (
    <div className="signet-trend">
      <div className="signet-trend-figures">
        {figures.map((f) => (
          <div key={f.label} className="signet-trend-figure">
            <span>{f.label}</span>
            <strong className={f.tone}>{f.value}</strong>
            {f.caption ? <small>{f.caption}</small> : null}
          </div>
        ))}
      </div>
      <div className="signet-trend-box">
        <div className="signet-trend-head">
          <span>{METRIC[metric].label} since it was added</span>
          <div className="signet-trend-switch" role="group" aria-label="Show on the chart">
            {(Object.keys(METRIC) as Metric[]).map((m) => (
              <button key={m} type="button" className={m === metric ? "on" : undefined} aria-pressed={m === metric} onClick={() => setMetric(m)}>
                {METRIC[m].label}
              </button>
            ))}
          </div>
        </div>
        {trend.isLoading ? (
          <div className="signet-trend-loading" aria-label="Loading" />
        ) : trend.isError || !trend.data ? (
          <p className="signet-trend-empty">Couldn't load this account's readings.</p>
        ) : (
          <TrendChart days={trend.data.days} metric={metric} />
        )}
      </div>
    </div>
  );
}
